import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Calendar, Clock, CheckCircle, XCircle, AlertCircle, User, Route, MapPin, Phone, Mail, ExternalLink, Package, LogIn, LogOut } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { formatTime12h } from "@/lib/time";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { sendOwnerPush } from "@/lib/owner-push";

interface PortalAppointment {
  id: string;
  service_id: string;
  service_name: string;
  start_datetime: string;
  end_datetime: string;
  status: string;
  confirmation_code: string;
  staff_id: string | null;
  staff_name: string | null;
  total_price_amount: number;
  currency: string;
}

interface PortalFoodOrder {
  id: string;
  created_at: string;
  status: string;
  confirmation_code: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  delivery_address: string | null;
  delivery_place_id: string | null;
  delivery_lat: number | null;
  delivery_lng: number | null;
  total_amount: number | null;
  currency: string | null;
  items: Array<{ id: string; item_name: string; unit_price: number; quantity: number; notes?: string | null }>;
}

interface StaffInfo {
  staff_id: string;
  business_id: string;
  portal_role: "service_provider" | "manager";
  name: string;
}

interface PortalRouteItem {
  id: string;
  completion_id: string | null;
  sequence_order?: number | null;
  schedule?: {
    id?: string;
    membership_id?: string;
    service_name: string;
    membership?: {
      id?: string;
      membership_code: string;
      customer?: {
        name: string;
      };
    };
  } | null;
  completion?: {
    id?: string;
    membership_id?: string;
    service_name: string;
    membership?: {
      id?: string;
      membership_code: string;
      customer?: {
        name: string;
      };
    };
  } | null;
}

interface PortalRoute {
  id: string;
  name: string;
  route_date: string;
  status: string;
  start_time: string | null;
  items?: PortalRouteItem[];
}

const sb = supabase as any;

export default function StaffPortal() {
  const { portalId } = useParams<{ portalId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState<StaffInfo | null>(null);
  const [appointments, setAppointments] = useState<PortalAppointment[]>([]);
  const [foodOrders, setFoodOrders] = useState<PortalFoodOrder[]>([]);
  const [businessModule, setBusinessModule] = useState<string | null>(null);
  const [routes, setRoutes] = useState<PortalRoute[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedAppointment, setSelectedAppointment] = useState<PortalAppointment | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editStatus, setEditStatus] = useState<string>("");
  const [editStaffId, setEditStaffId] = useState<string>("");
  const [isAppointmentViewOnly, setIsAppointmentViewOnly] = useState(false);
  const [portalInspirationMedia, setPortalInspirationMedia] = useState<{ file_path: string; media_type: string }[]>([]);
  const [availableStaff, setAvailableStaff] = useState<Array<{ id: string; name: string }>>([]);
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
  const [activeRoute, setActiveRoute] = useState<PortalRoute | null>(null);
  const [activeRouteItem, setActiveRouteItem] = useState<PortalRouteItem | null>(null);
  const [stopStatus, setStopStatus] = useState<"completed" | "missed" | "skipped" | "rescheduled">("completed");
  const [stopNotes, setStopNotes] = useState("");
  const [stopLocation, setStopLocation] = useState("");
  const [stopPhotos, setStopPhotos] = useState<File[]>([]);
  const [isCompletingStop, setIsCompletingStop] = useState(false);
  const stopPhotoInputRef = useRef<HTMLInputElement | null>(null);

  // Clock in/out: last event determines button label (clock_in -> show Clock out, else Clock in)
  const [lastClockEvent, setLastClockEvent] = useState<{ event_type: string; recorded_at: string } | null>(null);
  const [clockActionLoading, setClockActionLoading] = useState(false);

  useEffect(() => {
    if (!portalId) {
      navigate("/");
      return;
    }

    const loadPortalData = async () => {
      try {
        setLoading(true);

        // Validate portal access
        const { data: staffData, error: validateError } = await sb
          .rpc("validate_portal_access", { p_portal_id: portalId });

        if (validateError || !staffData || staffData.length === 0) {
          toast({
            title: "Invalid Portal Access",
            description: "This portal link is invalid or has been disabled.",
            variant: "destructive",
          });
          navigate("/");
          return;
        }

        const staff = staffData[0] as StaffInfo;
        setStaffInfo(staff);

        // Determine business primary module
        const { data: business, error: bizError } = await sb
          .from("businesses")
          .select("primary_module")
          .eq("id", staff.business_id)
          .single();

        const primaryModule = !bizError && business ? (business as any).primary_module || "appointments" : "appointments";
        setBusinessModule(primaryModule);

        if (primaryModule === "memberships") {
          // Load routes for membership operations
          setRoutesLoading(true);
          const today = new Date();
          const fromDate = new Date();
          fromDate.setDate(today.getDate() - 7);

          const { data: routesData, error: routesError } = await sb
            .from("service_routes")
            .select(`
              id,
              name,
              route_date,
              status,
              start_time,
              items:service_route_items(
                id,
                completion_id,
                sequence_order,
                schedule:membership_service_schedules(
                  id,
                  membership_id,
                  service_name,
                  membership:memberships(
                    id,
                    membership_code,
                    customer:customers(name)
                  )
                ),
                completion:service_completions(
                  id,
                  membership_id,
                  service_name,
                  membership:memberships(
                    id,
                    membership_code,
                    customer:customers(name)
                  )
                )
              )
            `)
            .eq("business_id", staff.business_id)
            .eq("assigned_staff_id", staff.staff_id)
            .gte("route_date", format(fromDate, "yyyy-MM-dd"))
            .order("route_date", { ascending: true });

          if (routesError) {
            logger.error("Failed to load portal routes", routesError as Error);
            toast({
              title: "Error",
              description: "Failed to load routes for this portal.",
              variant: "destructive",
            });
          } else {
            setRoutes((routesData || []) as PortalRoute[]);
          }
          setRoutesLoading(false);
        } else {
          if (primaryModule === "catering_services" || primaryModule === "food_vendors") {
            const { data: ordersData, error: ordersError } = await sb.rpc("get_portal_food_orders", {
              p_portal_id: portalId,
            });

            if (ordersError) {
              logger.error("Failed to load portal food orders", ordersError as Error);
              console.error("Portal food orders error:", ordersError);
              if (ordersError.message?.includes("function") || ordersError.code === "42883") {
                toast({
                  title: "Portal Not Configured",
                  description: "Delivery portal functions not found. Please run the database migration.",
                  variant: "destructive",
                });
              } else {
                throw ordersError;
              }
              return;
            }
            setFoodOrders((ordersData || []) as any);
          } else {
            // Load appointments (appointments module)
            const { data: appointmentsData, error: appointmentsError } = await sb
              .rpc("get_portal_appointments", { p_portal_id: portalId });

            if (appointmentsError) {
              logger.error("Failed to load portal appointments", appointmentsError as Error);
              console.error("Portal appointments error:", appointmentsError);
              // If function doesn't exist (migration not run), show helpful message
              if (appointmentsError.message?.includes("function") || appointmentsError.code === "42883") {
                toast({
                  title: "Portal Not Configured",
                  description: "Portal functions not found. Please run the database migration.",
                  variant: "destructive",
                });
              } else {
                throw appointmentsError;
              }
              return;
            }
            setAppointments(appointmentsData || []);
          }
        }

        // If manager, load available staff for reassignment
        if (staff.portal_role === "manager") {
          const { data: staffList, error: staffError } = await sb
            .from("staff")
            .select("id, name")
            .eq("business_id", staff.business_id)
            .eq("active", true)
            .order("name");

          if (!staffError && staffList) {
            setAvailableStaff(staffList);
          }
        }
      } catch (error) {
        logger.error("Failed to load portal data", error as Error);
        toast({
          title: "Error",
          description: "Failed to load portal data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPortalData();
  }, [portalId, navigate, toast]);

  // Fetch last clock event when portal is loaded (for Clock in / Clock out button state)
  useEffect(() => {
    if (!portalId || !staffInfo) return;
    sb.rpc("get_staff_last_clock_event", { p_portal_id: portalId })
      .then(({ data, error }) => {
        if (error) return;
        const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
        if (row && (row as any).event_type) {
          setLastClockEvent({ event_type: (row as any).event_type, recorded_at: (row as any).recorded_at });
        } else {
          setLastClockEvent(null);
        }
      })
      .catch(() => setLastClockEvent(null));
  }, [portalId, staffInfo]);

  const handleClockInOut = async () => {
    if (!portalId || clockActionLoading) return;
    const nextEventType = lastClockEvent?.event_type === "clock_in" ? "clock_out" : "clock_in";
    setClockActionLoading(true);
    try {
      let lat: number | null = null;
      let lng: number | null = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000, maximumAge: 60000 });
          });
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch {
          // ignore; record without geo
        }
      }
      const { data, error } = await sb.rpc("record_staff_clock_event", {
        p_portal_id: portalId,
        p_event_type: nextEventType,
        p_latitude: lat,
        p_longitude: lng,
      });
      if (error) throw error;
      const result = data as { ok?: boolean; error?: string } | null;
      if (result && !result.ok) throw new Error(result.error || "Failed to record");
      setLastClockEvent({ event_type: nextEventType, recorded_at: new Date().toISOString() });
      toast({
        title: nextEventType === "clock_in" ? "Clocked in" : "Clocked out",
        description: `Recorded at ${format(new Date(), "h:mm a")}`,
      });
    } catch (e) {
      logger.error("Clock in/out error", e as Error);
      toast({
        title: "Error",
        description: "Could not record clock. Try again.",
        variant: "destructive",
      });
    } finally {
      setClockActionLoading(false);
    }
  };

  const handleUpdateStatus = async (appointmentId: string, status: string) => {
    if (!portalId) return;

    // Find the appointment to check current status
    const appointment = appointments.find(apt => apt.id === appointmentId);
    if (appointment && isFinalStatus(appointment.status)) {
      toast({
        title: "Cannot Change Status",
        description: "Appointments with status 'completed', 'cancelled', or 'no_show' cannot have their status changed.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await sb
        .rpc("update_appointment_status_from_portal", {
          p_portal_id: portalId,
          p_appointment_id: appointmentId,
          p_status: status,
        });

      if (error) {
        logger.error("Failed to update status", error as Error);
        console.error("Update status error:", error);
        throw error;
      }

      toast({
        title: "Status updated",
        description: `Appointment marked as ${status}`,
      });

      void sendOwnerPush({
        businessId: staffInfo?.business_id,
        title: "Appointment status changed",
        body: `A staff member marked appointment ${appointment?.confirmation_code || appointmentId} as ${status.replace(/_/g, " ")}.`,
        url: "/app/appointments",
        data: {
          context: "appointment_status_changed",
          context_id: appointmentId,
          new_status: status,
          source: "staff_portal",
        },
      });

      // Reload appointments
      const { data: appointmentsData, error: appointmentsError } = await sb
        .rpc("get_portal_appointments", { p_portal_id: portalId });

      if (!appointmentsError && appointmentsData) {
        setAppointments(appointmentsData);
      }
    } catch (error) {
      logger.error("Failed to update status", error as Error);
      toast({
        title: "Error",
        description: "Failed to update appointment status",
        variant: "destructive",
      });
    }
  };

  const openAppointmentDialog = (appointment: PortalAppointment, viewOnly: boolean) => {
    setSelectedAppointment(appointment);
    setEditStatus(appointment.status);
    setEditStaffId(appointment.staff_id || "any");
    setIsAppointmentViewOnly(viewOnly);
    setPortalInspirationMedia([]);
    setIsEditDialogOpen(true);
    sb.rpc("get_portal_appointment_inspiration", {
      p_portal_id: portalId,
      p_appointment_id: appointment.id,
    })
      .then(({ data }) => setPortalInspirationMedia(Array.isArray(data) ? data : []))
      .catch(() => setPortalInspirationMedia([]));
  };

  const handleEditAppointment = (appointment: PortalAppointment) => {
    if (isFinalStatus(appointment.status)) {
      toast({
        title: "Cannot Edit",
        description: "Appointments with status 'completed', 'cancelled', or 'no_show' cannot be edited.",
        variant: "destructive",
      });
      return;
    }
    openAppointmentDialog(appointment, false);
  };

  const handleSaveEdit = async () => {
    if (!selectedAppointment || !portalId) return;

    setIsSaving(true);
    try {
      // Use combined update function (handles both status and staff assignment)
      const staffIdToUpdate = (staffInfo?.portal_role === "manager" && editStaffId !== selectedAppointment.staff_id) 
        ? (editStaffId === "any" ? null : editStaffId)
        : null;
      
      const { error } = await sb.rpc("update_appointment_from_portal", {
        p_portal_id: portalId,
        p_appointment_id: selectedAppointment.id,
        p_status: editStatus !== selectedAppointment.status ? editStatus : null,
        p_staff_id: staffIdToUpdate,
      });

      if (error) throw error;

      toast({
        title: "Appointment updated",
        description: "Changes have been saved",
      });

      setIsEditDialogOpen(false);
      setSelectedAppointment(null);

      // Reload appointments
      const { data: appointmentsData, error: appointmentsError } = await sb
        .rpc("get_portal_appointments", { p_portal_id: portalId });

      if (!appointmentsError && appointmentsData) {
        setAppointments(appointmentsData);
      }
    } catch (error) {
      logger.error("Failed to update appointment", error as Error);
      toast({
        title: "Error",
        description: "Failed to update appointment",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredAppointments = appointments.filter((apt) => {
    if (filterStatus === "all") return true;
    return apt.status === filterStatus;
  });

  const filteredFoodOrders = foodOrders.filter((o) => {
    if (filterStatus === "all") return true;
    return String(o.status || "") === filterStatus;
  });

  const openStopDialog = (route: PortalRoute, item: PortalRouteItem) => {
    setActiveRoute(route);
    setActiveRouteItem(item);
    setStopStatus("completed");
    setStopNotes("");
    setStopLocation("");
    setStopPhotos([]);
    setIsStopDialogOpen(true);
  };

  const handleCompleteStop = async () => {
    if (!staffInfo || !activeRoute || !activeRouteItem) return;

    const schedule = activeRouteItem.schedule;
    if (!schedule || !schedule.id || !schedule.membership_id) {
      toast({
        title: "Missing data",
        description: "This stop is missing schedule or membership information.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCompletingStop(true);

      // Upload photos if any
      const photoUrls: string[] = [];
      if (stopPhotos.length > 0) {
        for (const file of stopPhotos) {
          const fileExt = file.name.split(".").pop();
          const fileName = `service-completions/${staffInfo.business_id}/${schedule.id}/${Date.now()}-${Math.random()
            .toString(36)
            .slice(2)}.${fileExt}`;
          const { data: uploadData, error: uploadError } = await sb.storage
            .from("service-media")
            .upload(fileName, file, { cacheControl: "3600", upsert: false });
          if (uploadError) throw uploadError;
          const { data: urlData } = sb.storage.from("service-media").getPublicUrl(uploadData.path);
          if (urlData?.publicUrl) {
            photoUrls.push(urlData.publicUrl);
          }
        }
      }

      const completionData: any = {
        business_id: staffInfo.business_id,
        membership_id: schedule.membership_id,
        schedule_id: schedule.id,
        service_name: schedule.service_name,
        scheduled_date: activeRoute.route_date,
        status: stopStatus,
        completed_at: new Date().toISOString(),
        completed_by: staffInfo.staff_id,
      };

      if (stopNotes.trim()) {
        completionData.notes = stopNotes.trim();
      }
      if (stopLocation.trim()) {
        completionData.location_address = stopLocation.trim();
      }
      if (photoUrls.length > 0) {
        completionData.photos = photoUrls;
      }

      const { data: completion, error: completionError } = await sb
        .from("service_completions")
        .insert(completionData)
        .select()
        .single();

      if (completionError) throw completionError;

      // Link route item to this completion
      await sb
        .from("service_route_items")
        .update({ completion_id: completion.id })
        .eq("id", activeRouteItem.id);

      toast({
        title: "Stop updated",
        description: "Service has been marked as completed.",
      });

      setIsStopDialogOpen(false);
      setActiveRoute(null);
      setActiveRouteItem(null);

      // Reload routes
      // Reuse current businessModule/staffInfo context
      const today = new Date();
      const fromDate = new Date();
      fromDate.setDate(today.getDate() - 7);

      const { data: routesData } = await sb
        .from("service_routes")
        .select(`
          id,
          name,
          route_date,
          status,
          start_time,
          items:service_route_items(
            id,
            completion_id,
            sequence_order,
            schedule:membership_service_schedules(
              id,
              membership_id,
              service_name,
              membership:memberships(
                id,
                membership_code,
                customer:customers(name)
              )
            ),
            completion:service_completions(
              id,
              membership_id,
              service_name,
              membership:memberships(
                id,
                membership_code,
                customer:customers(name)
              )
            )
          )
        `)
        .eq("business_id", staffInfo.business_id)
        .eq("assigned_staff_id", staffInfo.staff_id)
        .gte("route_date", format(fromDate, "yyyy-MM-dd"))
        .order("route_date", { ascending: true });

      setRoutes((routesData || []) as PortalRoute[]);
    } catch (error: any) {
      logger.error("Failed to complete stop from portal", error as Error);
      toast({
        title: "Error",
        description: error?.message || "Failed to mark stop as completed.",
        variant: "destructive",
      });
    } finally {
      setIsCompletingStop(false);
    }
  };

  // Helper to check if appointment status is final (cannot be edited)
  const isFinalStatus = (status: string): boolean => {
    return status === "completed" || status === "cancelled" || status === "no_show";
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      confirmed: "default",
      completed: "secondary",
      no_show: "destructive",
      cancelled: "outline",
      new: "default",
      preparing: "secondary",
      ready: "outline",
      delivered: "secondary",
      not_delivered: "destructive",
    };
    return (
      <Badge variant={variants[status] || "outline"} className="capitalize w-fit whitespace-nowrap">
        {status.replace("_", " ")}
      </Badge>
    );
  };

  const mapsUrl = (args: { placeId?: string | null; address?: string | null; lat?: any; lng?: any }) => {
    const placeId = args.placeId ? String(args.placeId) : "";
    if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
    const lat = args.lat != null ? Number(args.lat) : null;
    const lng = args.lng != null ? Number(args.lng) : null;
    if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
    }
    const address = args.address ? String(args.address) : "";
    if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    return "";
  };

  const handleUpdateFoodOrderStatus = async (orderId: string, status: "delivered" | "not_delivered") => {
    if (!portalId) return;
    try {
      const current = (foodOrders || []).find((o: any) => String(o.id) === String(orderId));
      const currentStatus = String(current?.status || "");
      if (currentStatus === "delivered" || currentStatus === "not_delivered") {
        toast({ title: "Order is already finalized", description: "This order has been locked." });
        return;
      }
      const { error } = await sb.rpc("update_food_order_status_from_portal", {
        p_portal_id: portalId,
        p_order_id: orderId,
        p_status: status,
      });
      if (error) throw error;

      toast({ title: "Updated", description: `Order marked as ${status.replace("_", " ")}` });

      void sendOwnerPush({
        businessId: staffInfo?.business_id,
        title: "Food order status changed",
        body: `Order ${current?.confirmation_code ? `#${current.confirmation_code} ` : ""}is now ${status.replace(/_/g, " ")}.`,
        url: "/app/orders",
        data: {
          context: "food_order_status_changed",
          context_id: orderId,
          new_status: status,
          source: "staff_portal",
        },
      });

      const { data: ordersData } = await sb.rpc("get_portal_food_orders", { p_portal_id: portalId });
      setFoodOrders((ordersData || []) as any);
    } catch (e: any) {
      toast({ title: "Error", description: e?.message || "Failed to update order", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (!staffInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invalid Portal Access</h2>
            <p className="text-muted-foreground">
              This portal link is invalid or has been disabled.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-4 sm:py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                {businessModule === "catering_services" || businessModule === "food_vendors" ? "Delivery Portal" : "Staff Portal"}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Welcome, {staffInfo.name} •{" "}
                {businessModule === "catering_services" || businessModule === "food_vendors"
                  ? staffInfo.portal_role === "manager"
                    ? "Dispatcher"
                    : "Rider"
                  : staffInfo.portal_role === "manager"
                    ? "Manager"
                    : "Service Provider"}
              </p>
            </div>
            <Badge variant="outline" className="text-xs sm:text-sm w-fit">
              Portal ID: {portalId}
            </Badge>
          </div>
          {/* Clock in / Clock out */}
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant={lastClockEvent?.event_type === "clock_in" ? "secondary" : "default"}
              size="sm"
              onClick={handleClockInOut}
              disabled={clockActionLoading}
              className="gap-2"
            >
              {clockActionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : lastClockEvent?.event_type === "clock_in" ? (
                <LogOut className="h-4 w-4" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
              {lastClockEvent?.event_type === "clock_in" ? "Clock out" : "Clock in"}
            </Button>
            {lastClockEvent && (
              <span className="text-xs text-muted-foreground">
                Last: {lastClockEvent.event_type.replace("_", " ")} at {format(new Date(lastClockEvent.recorded_at), "h:mm a")}
              </span>
            )}
          </div>
        </div>

        {/* Membership routes view */}
        {businessModule === "memberships" ? (
          <div className="space-y-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                <Route className="h-4 w-4" />
                Today's and upcoming routes
              </h2>
            </div>
            {routesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : routes.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Route className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No routes assigned</h3>
                  <p className="text-muted-foreground">
                    You don't have any routes assigned to you yet.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {routes.map((route) => {
                  const stops = [...(route.items || [])].sort(
                    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
                  );
                  return (
                    <Card key={route.id}>
                      <CardContent className="p-4 sm:p-6 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <h3 className="text-base sm:text-lg font-semibold">{route.name}</h3>
                            <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-muted-foreground">
                              <div className="flex items-center gap-1.5">
                                <Calendar className="h-4 w-4" />
                                <span>{format(new Date(route.route_date), "EEE, MMM d, yyyy")}</span>
                              </div>
                              {route.start_time && (
                                <div className="flex items-center gap-1.5">
                                  <Clock className="h-4 w-4" />
                                  <span>{route.start_time}</span>
                                </div>
                              )}
                              {stops.length > 0 && (
                                <span>
                                  {stops.length} stop{stops.length !== 1 ? "s" : ""} on this route
                                </span>
                              )}
                            </div>
                          </div>
                          <Badge className="capitalize" variant="outline">
                            {route.status.replace("_", " ")}
                          </Badge>
                        </div>
                        {stops.length > 0 && (
                          <div className="border-t border-border pt-2 mt-1 space-y-1">
                            {stops.map((item: any, index: number) => {
                              const serviceName =
                                item.schedule?.service_name || item.completion?.service_name || "Service";
                              const memberName =
                                item.schedule?.membership?.customer?.name ||
                                item.completion?.membership?.customer?.name ||
                                null;
                              const isDone = !!item.completion_id;
                              return (
                                <div
                                  key={item.id || index}
                                  className="flex items-center justify-between gap-3 text-xs sm:text-sm"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span
                                      className={`h-5 w-5 flex items-center justify-center rounded-full border text-[11px] font-medium flex-shrink-0 ${
                                        isDone
                                          ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                          : "bg-background border-muted-foreground/40 text-muted-foreground"
                                      }`}
                                    >
                                      {index + 1}
                                    </span>
                                    <div className="min-w-0">
                                      <p
                                        className={`font-medium truncate ${
                                          isDone ? "line-through text-muted-foreground" : ""
                                        }`}
                                      >
                                        {serviceName}
                                      </p>
                                      {memberName && (
                                        <p className="text-[11px] text-muted-foreground truncate">
                                          {memberName}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {!isDone && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-[11px] px-2"
                                      onClick={() => openStopDialog(route, item)}
                                    >
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Complete
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        ) : businessModule === "catering_services" || businessModule === "food_vendors" ? (
          <>
            {/* Delivery orders */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[220px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="preparing">Preparing</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="not_delivered">Not delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {filteredFoodOrders.length} order{filteredFoodOrders.length !== 1 ? "s" : ""}
              </p>
            </div>

            {filteredFoodOrders.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No orders found</h3>
                  <p className="text-muted-foreground">
                    {filterStatus === "all" ? "You don't have any orders yet." : `No ${filterStatus} orders found.`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredFoodOrders.map((o) => {
                  const isDelivery = !!(o.delivery_address || o.delivery_place_id);
                  const isFoodFinal = String(o.status) === "delivered" || String(o.status) === "not_delivered";
                  const maps = mapsUrl({
                    placeId: o.delivery_place_id,
                    address: o.delivery_address,
                    lat: o.delivery_lat,
                    lng: o.delivery_lng,
                  });
                  const currency = String(o.currency || "GHS");
                  return (
                    <Card key={o.id}>
                      <CardContent className="p-4 sm:p-6 space-y-4">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="min-w-0 space-y-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="font-semibold text-foreground">
                                {o.confirmation_code ? `#${o.confirmation_code}` : "Order"}
                              </div>
                              <Badge variant={isDelivery ? "secondary" : "outline"} className="whitespace-nowrap">
                                {isDelivery ? "Delivery" : "Pickup"}
                              </Badge>
                              {getStatusBadge(String(o.status || "new"))}
                              {o.total_amount != null ? (
                                <Badge variant="outline">{currency} {Number(o.total_amount || 0).toFixed(2)}</Badge>
                              ) : null}
                            </div>

                            <div className="rounded-xl border border-border bg-muted/20 p-3 sm:p-4 space-y-2">
                              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer</div>
                              <div className="grid gap-1.5 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2 min-w-0">
                                  <User className="h-4 w-4 flex-shrink-0" />
                                  <span className="text-foreground font-medium truncate">{o.customer_name || "Customer"}</span>
                                </div>
                                {o.customer_phone ? (
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Phone className="h-4 w-4 flex-shrink-0" />
                                    <span className="truncate">{o.customer_phone}</span>
                                  </div>
                                ) : null}
                                {o.customer_email ? (
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Mail className="h-4 w-4 flex-shrink-0" />
                                    <span className="truncate">{o.customer_email}</span>
                                  </div>
                                ) : null}
                              </div>

                              {isDelivery && o.delivery_address ? (
                                <div className="pt-2 border-t border-border/60">
                                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Delivery</div>
                                  <div className="mt-1 flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2 min-w-0 text-sm text-muted-foreground">
                                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                      <span className="line-clamp-2">{o.delivery_address}</span>
                                    </div>
                                    {maps ? (
                                      <a
                                        href={maps}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline flex-shrink-0"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                        Maps
                                      </a>
                                    ) : null}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>

                          {isDelivery ? (
                            <div className="flex flex-col gap-2 w-full lg:w-[240px]">
                              <Button
                                type="button"
                                size="sm"
                                className="w-full"
                                onClick={() => handleUpdateFoodOrderStatus(o.id, "delivered")}
                                disabled={isFoodFinal}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Delivered
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="w-full text-destructive hover:text-destructive"
                                onClick={() => handleUpdateFoodOrderStatus(o.id, "not_delivered")}
                                disabled={isFoodFinal}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Not delivered
                              </Button>
                              {isFoodFinal ? (
                                <p className="text-xs text-muted-foreground text-center">Locked after final status.</p>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
                          <div className="text-sm font-semibold text-foreground">Items</div>
                          {(o.items || []).length === 0 ? (
                            <div className="text-sm text-muted-foreground">No items.</div>
                          ) : (
                            <div className="space-y-2">
                              {(o.items || []).map((it: any) => (
                                <div key={it.id} className="flex items-start justify-between gap-4 text-sm">
                                  <div className="min-w-0">
                                    <div className="font-medium text-foreground">{it.item_name}</div>
                                    <div className="mt-0.5 text-xs text-muted-foreground">
                                      Qty: {Number(it.quantity || 1)} • Unit: {currency} {Number(it.unit_price || 0).toFixed(2)}
                                    </div>
                                    {it.notes ? <div className="text-xs text-muted-foreground mt-0.5">Note: {it.notes}</div> : null}
                                  </div>
                                  <div className="font-semibold text-foreground whitespace-nowrap">
                                    {currency} {(Number(it.unit_price || 0) * Number(it.quantity || 1)).toFixed(2)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Filters */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Bookings</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {filteredAppointments.length} booking{filteredAppointments.length !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Appointments List */}
            {filteredAppointments.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No bookings found</h3>
                  <p className="text-muted-foreground">
                    {filterStatus === "all"
                      ? "You don't have any bookings yet."
                      : `No ${filterStatus} bookings found.`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredAppointments.map((apt) => (
                  <Card key={apt.id}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            <h3 className="text-base sm:text-lg font-semibold">{apt.service_name}</h3>
                            {getStatusBadge(apt.status)}
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-4 w-4 flex-shrink-0" />
                              <span className="whitespace-nowrap">
                                {format(new Date(apt.start_datetime), "EEE, MMM d, yyyy")}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-4 w-4 flex-shrink-0" />
                              <span className="whitespace-nowrap">
                                {formatTime12h(format(new Date(apt.start_datetime), "HH:mm"))} -{" "}
                                {formatTime12h(format(new Date(apt.end_datetime), "HH:mm"))}
                              </span>
                            </div>
                            {apt.staff_name && (
                              <div className="flex items-center gap-1.5">
                                <User className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{apt.staff_name}</span>
                              </div>
                            )}
                          </div>
                          {apt.confirmation_code && (
                            <p className="text-xs text-muted-foreground font-mono">
                              Code: {apt.confirmation_code}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2 pt-2 sm:pt-0 border-t sm:border-t-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAppointmentDialog(apt, true)}
                            className="w-full sm:w-auto"
                          >
                            View
                          </Button>
                          {staffInfo.portal_role === "manager" && !isFinalStatus(apt.status) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditAppointment(apt)}
                              className="w-full sm:w-auto"
                            >
                              Edit
                            </Button>
                          )}
                          {apt.status === "confirmed" && !isFinalStatus(apt.status) && (
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateStatus(apt.id, "completed")}
                                className="text-green-600 hover:text-green-700 w-full sm:w-auto"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Complete
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateStatus(apt.id, "no_show")}
                                className="text-red-600 hover:text-red-700 w-full sm:w-auto"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                No Show
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Complete Stop Dialog (memberships mode) */}
        {businessModule === "memberships" && (
          <Dialog open={isStopDialogOpen} onOpenChange={setIsStopDialogOpen}>
            <DialogContent className="max-w-[95vw] sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Complete Service</DialogTitle>
                <DialogDescription>
                  Mark this stop as completed, missed, skipped, or rescheduled and optionally add photos.
                </DialogDescription>
              </DialogHeader>
              {activeRoute && activeRouteItem && (
                <div className="space-y-4 py-2">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {activeRouteItem.schedule?.service_name || activeRouteItem.completion?.service_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activeRouteItem.schedule?.membership?.customer?.name ||
                        activeRouteItem.completion?.membership?.customer?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(activeRoute.route_date), "EEE, MMM d, yyyy")}{" "}
                      {activeRoute.start_time ? `• ${activeRoute.start_time}` : ""}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={stopStatus} onValueChange={(v) => setStopStatus(v as any)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="missed">Missed</SelectItem>
                        <SelectItem value="skipped">Skipped</SelectItem>
                        <SelectItem value="rescheduled">Rescheduled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Location (optional)</Label>
                    <Input
                      value={stopLocation}
                      onChange={(e) => setStopLocation(e.target.value)}
                      placeholder="Address where service was performed"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={stopNotes}
                      onChange={(e) => setStopNotes(e.target.value)}
                      rows={3}
                      placeholder="Add any notes about this visit..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Photos (optional)</Label>
                    <input
                      ref={stopPhotoInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setStopPhotos(files);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => stopPhotoInputRef.current?.click()}
                    >
                      Upload Photos
                    </Button>
                    {stopPhotos.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {stopPhotos.length} photo{stopPhotos.length > 1 ? "s" : ""} selected
                      </p>
                    )}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsStopDialogOpen(false)}
                  disabled={isCompletingStop}
                >
                  Cancel
                </Button>
                <Button type="button" onClick={handleCompleteStop} disabled={isCompletingStop}>
                  {isCompletingStop && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Mark {stopStatus}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Appointment View/Edit Dialog (appointments mode: all staff can View; managers can Edit) */}
        {businessModule !== "memberships" &&
          businessModule !== "catering_services" &&
          businessModule !== "food_vendors" && (
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="max-w-[95vw] sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{isAppointmentViewOnly ? "Appointment Details" : "Edit Appointment"}</DialogTitle>
                <DialogDescription>
                  {isAppointmentViewOnly
                    ? "View appointment and client inspiration"
                    : "Update appointment status and staff assignment"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Service</Label>
                  <p className="text-sm font-medium">{selectedAppointment?.service_name}</p>
                </div>
                <div className="space-y-2">
                  <Label>Date & Time</Label>
                  <p className="text-sm">
                    {selectedAppointment && (
                      <>
                        {format(new Date(selectedAppointment.start_datetime), "EEE, MMM d, yyyy")} •{" "}
                        {formatTime12h(format(new Date(selectedAppointment.start_datetime), "HH:mm"))} -{" "}
                        {formatTime12h(format(new Date(selectedAppointment.end_datetime), "HH:mm"))}
                      </>
                    )}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Service provider</Label>
                  <p className="text-sm font-medium">{selectedAppointment?.staff_name || "Not assigned"}</p>
                </div>
                {portalInspirationMedia.length > 0 && (
                  <div className="space-y-2">
                    <Label>Client inspiration</Label>
                    <div className="flex flex-wrap gap-2">
                      {portalInspirationMedia.map((m, i) => {
                        const publicUrl = supabase.storage.from("appointment-inspiration").getPublicUrl(m.file_path).data.publicUrl;
                        return m.media_type === "image" ? (
                          <a
                            key={i}
                            href={publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded-lg border border-border overflow-hidden w-28 h-28 sm:w-32 sm:h-32 shrink-0 hover:opacity-90"
                          >
                            <img src={publicUrl} alt="" className="w-full h-full object-cover" />
                          </a>
                        ) : (
                          <a
                            key={i}
                            href={publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center rounded-lg border border-border w-28 h-28 sm:w-32 sm:h-32 shrink-0 bg-muted text-muted-foreground text-xs hover:bg-muted/80"
                          >
                            Video
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                {!isAppointmentViewOnly && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="edit-status">Status</Label>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger id="edit-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="no_show">No Show</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-staff">Assign to Staff</Label>
                      <Select value={editStaffId} onValueChange={setEditStaffId}>
                        <SelectTrigger id="edit-staff">
                          <SelectValue placeholder="Select staff member" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any Available</SelectItem>
                          {availableStaff.map((staff) => (
                            <SelectItem key={staff.id} value={staff.id}>
                              {staff.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {isAppointmentViewOnly ? "Close" : "Cancel"}
                </Button>
                {!isAppointmentViewOnly && (
                  <Button onClick={handleSaveEdit} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
